import { VIETNAM_DIVISIONS } from './vietnamDivisions'

export function removeVietnameseAccents(str: string): string {
  if (!str) return ''
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim()
}

export function cleanAdminPrefix(name: string): string {
  if (!name) return ''
  return name
    .replace(
      /^(Thành phố|Tỉnh|Quận|Huyện|Thị xã|Phường|Xã|Thị trấn|TP\b|TP\.|Q\b|Q\.|H\.|H\s+|P\b|P\.|TX\b|TX\.|TT\b|TT\.)\s*/i,
      '',
    )
    .trim()
}

export function isWardName(name: string): boolean {
  if (!name) return false
  const trimmed = name.trim()
  return /^(Phường|Xã|Thị trấn|P\.|TT\.)\s+/i.test(trimmed)
}

export function isDistrictName(name: string): boolean {
  if (!name) return false
  const trimmed = name.trim()
  return /^(Quận|Huyện|Thị xã|Q\.|H\.|TX\.)\s+/i.test(trimmed)
}

const PROVINCE_ALIASES: Record<string, string> = {
  hcm: 'Thành phố Hồ Chí Minh',
  tphcm: 'Thành phố Hồ Chí Minh',
  'tp hcm': 'Thành phố Hồ Chí Minh',
  'tp.hcm': 'Thành phố Hồ Chí Minh',
  'sai gon': 'Thành phố Hồ Chí Minh',
  saigon: 'Thành phố Hồ Chí Minh',
  'ho chi minh': 'Thành phố Hồ Chí Minh',
  hn: 'Thành phố Hà Nội',
  'ha noi': 'Thành phố Hà Nội',
  'thu do ha noi': 'Thành phố Hà Nội',
  'tp ha noi': 'Thành phố Hà Nội',
  'tp. ha noi': 'Thành phố Hà Nội',
  'da nang': 'Thành phố Đà Nẵng',
  'hai phong': 'Thành phố Hải Phòng',
  'can tho': 'Thành phố Cần Thơ',
}

/**
 * Tìm Tỉnh/Thành phố chuẩn từ chuỗi đầu vào
 */
export function findProvince(query?: string | null): string | null {
  if (!query || !query.trim()) return null
  const qNorm = removeVietnameseAccents(query)
  const qClean = removeVietnameseAccents(cleanAdminPrefix(query))
  const qNoSpace = qNorm.replace(/[\s\.\-_]/g, '')

  // 1. Alias match
  if (PROVINCE_ALIASES[qNorm] || PROVINCE_ALIASES[qClean] || PROVINCE_ALIASES[qNoSpace]) {
    return PROVINCE_ALIASES[qNorm] || PROVINCE_ALIASES[qClean] || PROVINCE_ALIASES[qNoSpace] || null
  }

  // 2. Exact or clean match
  for (const prov of Object.keys(VIETNAM_DIVISIONS)) {
    const pNorm = removeVietnameseAccents(prov)
    const pClean = removeVietnameseAccents(cleanAdminPrefix(prov))
    if (pNorm === qNorm || pClean === qClean) return prov
    if (pNorm.replace(/[\s\.\-_]/g, '') === qNoSpace) return prov
  }

  // 3. Substring match
  for (const prov of Object.keys(VIETNAM_DIVISIONS)) {
    const pNorm = removeVietnameseAccents(prov)
    const pClean = removeVietnameseAccents(cleanAdminPrefix(prov))
    if (qNorm.includes(pClean) || pNorm.includes(qClean)) return prov
  }

  return null
}

/**
 * Tìm Quận/Huyện trong một Tỉnh
 */
export function findDistrictInProvince(provinceName: string, query?: string | null): string | null {
  if (!query || !query.trim()) return null
  const districts = VIETNAM_DIVISIONS[provinceName]
  if (!districts) return null

  const qNorm = removeVietnameseAccents(query)
  const qClean = removeVietnameseAccents(cleanAdminPrefix(query))
  const qNoSpace = qClean.replace(/[\s\.\-_]/g, '')

  for (const dist of Object.keys(districts)) {
    const dNorm = removeVietnameseAccents(dist)
    const dClean = removeVietnameseAccents(cleanAdminPrefix(dist))
    if (dNorm === qNorm || dClean === qClean) return dist
    if (dClean.replace(/[\s\.\-_]/g, '') === qNoSpace) return dist
  }

  for (const dist of Object.keys(districts)) {
    const dClean = removeVietnameseAccents(cleanAdminPrefix(dist))
    if (qClean.length >= 3 && (dClean.includes(qClean) || qClean.includes(dClean))) return dist
  }

  return null
}

/**
 * Suy luận Quận/Huyện từ Phường/Xã và Tỉnh
 * (Ví dụ: "Xã Quảng Bị" + "Thành phố Hà Nội" -> "Huyện Chương Mỹ")
 */
export function findDistrictByWardAndProvince(
  provinceName: string,
  wardQuery?: string | null
): { district: string; ward: string } | null {
  if (!wardQuery || !wardQuery.trim()) return null
  const districts = VIETNAM_DIVISIONS[provinceName]
  if (!districts) return null

  const wNorm = removeVietnameseAccents(wardQuery)
  const wClean = removeVietnameseAccents(cleanAdminPrefix(wardQuery))
  const wNoSpace = wClean.replace(/[\s\.\-_]/g, '')

  // 1. Match chính xác (cả tên hoặc cleanName)
  for (const [dist, wards] of Object.entries(districts)) {
    for (const w of wards) {
      const wardNorm = removeVietnameseAccents(w)
      const wardClean = removeVietnameseAccents(cleanAdminPrefix(w))
      if (wardNorm === wNorm || wardClean === wClean) {
        return { district: dist, ward: w }
      }
      if (wardClean.replace(/[\s\.\-_]/g, '') === wNoSpace) {
        return { district: dist, ward: w }
      }
    }
  }

  // 2. Match chuỗi con nếu độ dài hợp lý
  if (wClean.length >= 3) {
    for (const [dist, wards] of Object.entries(districts)) {
      for (const w of wards) {
        const wardClean = removeVietnameseAccents(cleanAdminPrefix(w))
        if (wardClean.includes(wClean) || wClean.includes(wardClean)) {
          return { district: dist, ward: w }
        }
      }
    }
  }

  return null
}

/**
 * Tìm Phường/Xã trong một Quận/Huyện cụ thể
 */
export function findWardInDistrict(
  provinceName: string,
  districtName: string,
  query?: string | null
): string | null {
  if (!query || !query.trim()) return null
  const districts = VIETNAM_DIVISIONS[provinceName]
  if (!districts) return null
  const wards = districts[districtName]
  if (!wards) return null

  const qNorm = removeVietnameseAccents(query)
  const qClean = removeVietnameseAccents(cleanAdminPrefix(query))
  const qNoSpace = qClean.replace(/[\s\.\-_]/g, '')

  for (const w of wards) {
    const wNorm = removeVietnameseAccents(w)
    const wClean = removeVietnameseAccents(cleanAdminPrefix(w))
    if (wNorm === qNorm || wClean === qClean) return w
    if (wClean.replace(/[\s\.\-_]/g, '') === qNoSpace) return w
  }

  if (qClean.length >= 3) {
    for (const w of wards) {
      const wClean = removeVietnameseAccents(cleanAdminPrefix(w))
      if (wClean.includes(qClean) || wClean.includes(wClean)) return w
    }
  }

  return null
}

export interface ResolvedAddress {
  city: string
  cleanCity: string
  district: string
  cleanDistrict: string
  ward: string
  cleanWard: string
  street: string
  formattedAddress: string
  badges: { type: 'street' | 'ward' | 'district' | 'city'; label: string }[]
}

/**
 * Bộ suy diễn và giải quyết triệt để địa chỉ hành chính 3 cấp và cấp chi tiết
 */
export function resolveAdministrativeAddress(params: {
  city?: string | null
  district?: string | null
  ward?: string | null
  street?: string | null
  fullAddress?: string | null
}): ResolvedAddress {
  let rawCity = (params.city || '').trim()
  let rawDistrict = (params.district || '').trim()
  let rawWard = (params.ward || '').trim()
  let rawStreet = (params.street || '').trim()
  const fullAddress = (params.fullAddress || '').trim()

  // 1. Phân giải Tỉnh/Thành phố
  let province = findProvince(rawCity) || findProvince(fullAddress) || rawCity

  if (!province && fullAddress) {
    const parts = fullAddress.split(',').map((p) => p.trim())
    for (let i = parts.length - 1; i >= 0; i--) {
      const found = findProvince(parts[i])
      if (found) {
        province = found
        break
      }
    }
  }

  // 2. Phát hiện nhầm lẫn vai trò
  // 2A: Nếu rawWard thực chất là Quận/Huyện (VD: Nominatim gán "Phường Hoàn Kiếm", "Phường Ba Đình", "Phường Cầu Giấy")
  if (province && VIETNAM_DIVISIONS[province] && rawWard) {
    const distFoundFromWard = findDistrictInProvince(province, rawWard)
    if (distFoundFromWard) {
      const isRealWard = findWardInDistrict(province, distFoundFromWard, rawWard)
      if (!isRealWard) {
        // Tên này thực chất là Quận/Huyện chứ không phải Phường/Xã
        if (!rawDistrict) rawDistrict = distFoundFromWard
        rawWard = ''
      }
    }
  }

  // 2B: Nếu rawDistrict thực chất là Phường/Xã (VD: Nominatim gán "Xã Quảng Bị" vào district)
  if (rawDistrict && isWardName(rawDistrict) && !rawWard) {
    rawWard = rawDistrict
    rawDistrict = ''
  } else if (rawDistrict && isWardName(rawDistrict) && rawWard && !isWardName(rawWard)) {
    const temp = rawWard
    rawWard = rawDistrict
    rawDistrict = temp
  }

  // 3. Phân giải Quận/Huyện và Phường/Xã
  let matchedDistrict: string = ''
  let matchedWard: string = ''

  if (province && VIETNAM_DIVISIONS[province]) {
    // 3A. Nếu có ward nhưng thiếu district -> Suy luận district từ ward!
    if (rawWard && (!rawDistrict || rawDistrict === rawWard)) {
      const deduced = findDistrictByWardAndProvince(province, rawWard)
      if (deduced) {
        matchedDistrict = deduced.district
        matchedWard = deduced.ward
      } else {
        matchedWard = rawWard
      }
    } else if (rawDistrict) {
      // 3B. Có district
      const foundDist = findDistrictInProvince(province, rawDistrict)
      if (foundDist) {
        matchedDistrict = foundDist
        if (rawWard) {
          const foundWard = findWardInDistrict(province, matchedDistrict, rawWard)
          matchedWard = foundWard || rawWard
        } else if (fullAddress) {
          const distWards = VIETNAM_DIVISIONS[province]?.[matchedDistrict] || []
          for (const w of distWards) {
            const wClean = removeVietnameseAccents(cleanAdminPrefix(w))
            if (wClean.length >= 3 && removeVietnameseAccents(fullAddress).includes(wClean)) {
              matchedWard = w
              break
            }
          }
        }
      } else {
        const deduced = findDistrictByWardAndProvince(province, rawDistrict)
        if (deduced) {
          matchedDistrict = deduced.district
          matchedWard = deduced.ward
        } else {
          matchedDistrict = rawDistrict
          matchedWard = rawWard
        }
      }
    } else if (fullAddress) {
      const dists = VIETNAM_DIVISIONS[province] || {}
      for (const [dist, wards] of Object.entries(dists)) {
        for (const w of wards) {
          const wClean = removeVietnameseAccents(cleanAdminPrefix(w))
          if (wClean.length >= 3 && removeVietnameseAccents(fullAddress).includes(wClean)) {
            matchedWard = w
            matchedDistrict = dist
            break
          }
        }
        if (matchedDistrict) break
      }
    }
  } else {
    matchedDistrict = rawDistrict
    matchedWard = rawWard
  }

  // 4. Chuẩn hóa cấp chi tiết (Street / Số nhà / Thôn xóm / POI)
  let cleanStreet = rawStreet
  if (cleanStreet) {
    const toRemove = [matchedWard, matchedDistrict, province, 'Việt Nam', 'Vietnam']
    for (const item of toRemove) {
      if (item && item.length >= 2) {
        const regex = new RegExp(`(^|,|\\s)${item}(\\s|,|$)`, 'gi')
        cleanStreet = cleanStreet.replace(regex, ' ').trim()
      }
    }
    cleanStreet = cleanStreet.replace(/^[,-\s]+|[,-\s]+$/g, '').trim()
  }

  // 5. Xây dựng formattedAddress chuẩn mực
  const addressParts = [
    cleanStreet,
    matchedWard,
    matchedDistrict,
    province,
  ].filter((p): p is string => Boolean(p && p.trim()))

  const finalFormatted = addressParts.join(', ')

  // 6. Xây dựng Badges
  const badges: { type: 'street' | 'ward' | 'district' | 'city'; label: string }[] = []
  if (cleanStreet) badges.push({ type: 'street', label: cleanStreet })
  if (matchedWard) badges.push({ type: 'ward', label: matchedWard })
  if (matchedDistrict) badges.push({ type: 'district', label: matchedDistrict })
  if (province) badges.push({ type: 'city', label: province })

  return {
    city: province,
    cleanCity: cleanAdminPrefix(province),
    district: matchedDistrict,
    cleanDistrict: cleanAdminPrefix(matchedDistrict),
    ward: matchedWard,
    cleanWard: cleanAdminPrefix(matchedWard),
    street: cleanStreet,
    formattedAddress: finalFormatted || fullAddress,
    badges,
  }
}

